// شرح حال — the seven typed sections, and the two that carry structured rows.
//
// ── The set is CLOSED, and closed in a way that fails silently if broken ───
//
// `section` ∈ { chief_complaint, hpi, pmh, medications, allergies, vitals,
// labs }. The BFF rejects an eighth value with INVALID_INPUT — but the reason
// the set is closed is worse than a rejection: the BFF's prompt builder emits
// exactly these seven, in this order, when it folds the case into the teaching
// brief. A section it does not know has nowhere to go in that brief, so it
// would be QUIETLY DROPPED — the student's typing would never reach the model,
// and the answer would look merely wrong rather than broken
// (student-app/docs/CORE.md §10, rule 6).
//
// So this array is not a UI convenience. It is the client's copy of a contract,
// and ORDER IS PART OF IT: the editor presents the sections in the order a
// history is taken, which is the order the prompt reads them in.
//
// Pure and React-free — `labelKey` rather than `label`, because this list is
// module-level and cannot call a hook. tests/history.test.js calls it directly.

import {
  MessageSquareWarning, FileText, History, Pill, TriangleAlert, Activity, FlaskConical,
} from 'lucide-react';

export const HISTORY_SECTIONS = [
  { id: 'chief_complaint', labelKey: 'history.sections.chiefComplaint', Icon: MessageSquareWarning },
  { id: 'hpi',             labelKey: 'history.sections.hpi',            Icon: FileText },
  { id: 'pmh',             labelKey: 'history.sections.pmh',            Icon: History },
  { id: 'medications',     labelKey: 'history.sections.medications',    Icon: Pill },
  { id: 'allergies',       labelKey: 'history.sections.allergies',      Icon: TriangleAlert },
  { id: 'vitals',          labelKey: 'history.sections.vitals',         Icon: Activity },
  { id: 'labs',            labelKey: 'history.sections.labs',           Icon: FlaskConical },
];

/** The ids alone, in the fixed clinical order. */
export const SECTION_IDS = HISTORY_SECTIONS.map((s) => s.id);

/** The section the editor opens on when the URL names none. */
export const DEFAULT_SECTION = 'chief_complaint';

/**
 * Validate a `:section` URL segment.
 *
 * A hand-edited or stale URL must land on a real section rather than render an
 * editor for a section the server will reject on save — the rejection would
 * arrive only AFTER the student had typed a history into it.
 */
export const isSection = (id) => SECTION_IDS.includes(id);
export const resolveSection = (id) => (isSection(id) ? id : DEFAULT_SECTION);

/**
 * The two sections that carry structured rows alongside their prose.
 *
 * Structured data is an ADDITION to `body`, never a replacement: the prose is
 * what a student actually writes and what carries the teaching value. An entry
 * with an empty body is legitimate only when `structured` is populated.
 */
export const STRUCTURED_SECTIONS = ['vitals', 'labs'];
export const hasStructured = (section) => STRUCTURED_SECTIONS.includes(section);

// ── vitals ──────────────────────────────────────────────────────────────────
//
// `{ "bp": "120/80", "hr": 72, "rr": 16, "temp": 37.1, "spo2": 98 }`
//
// `bp` is a STRING and the rest are numbers, and that split is the shape the
// frozen schema documents. Blood pressure is two numbers with a slash and has
// no honest numeric representation; the others are single readings a chart can
// plot. Anything the student left blank is OMITTED from the object rather than
// sent as null — the payload is stored opaque and interpolated into a prompt, so
// `"hr": null` would reach the model as a fact ("heart rate: null") instead of
// as an absence.

export const VITAL_FIELDS = [
  { key: 'bp',   labelKey: 'history.vitals.bp',   unitKey: null,                     numeric: false, placeholder: '120/80' },
  { key: 'hr',   labelKey: 'history.vitals.hr',   unitKey: 'history.units.bpm',      numeric: true },
  { key: 'rr',   labelKey: 'history.vitals.rr',   unitKey: 'history.units.perMin',   numeric: true },
  { key: 'temp', labelKey: 'history.vitals.temp', unitKey: 'history.units.celsius',  numeric: true },
  { key: 'spo2', labelKey: 'history.vitals.spo2', unitKey: 'history.units.percent',  numeric: true },
];

/** A blank vitals form — every field a string, because that is what an input holds. */
export const emptyVitals = () =>
  Object.fromEntries(VITAL_FIELDS.map((f) => [f.key, '']));

/** Stored payload → form values. Numbers become strings; absent stays ''. */
export function vitalsToForm(structured) {
  const src = structured && typeof structured === 'object' && !Array.isArray(structured) ? structured : {};
  return Object.fromEntries(
    VITAL_FIELDS.map((f) => [f.key, src[f.key] == null ? '' : String(src[f.key])]),
  );
}

/**
 * Form values → the stored payload, or null when the student filled nothing in.
 *
 * null rather than `{}`: an empty object is a payload that says "vitals were
 * recorded and every one of them was empty", which is not what a blank form
 * means, and it would be rendered as an empty vitals card forever.
 */
export function formToVitals(form) {
  const out = {};
  for (const f of VITAL_FIELDS) {
    const raw = String(form?.[f.key] ?? '').trim();
    if (!raw) continue;
    if (!f.numeric) { out[f.key] = raw; continue; }
    const n = Number(raw);
    // A value that will not parse is kept as TEXT rather than dropped. It is
    // the student's reading; refusing to store «۳۷/۲» silently would lose it,
    // and the store validates only that the payload is JSON.
    out[f.key] = Number.isFinite(n) ? n : raw;
  }
  return Object.keys(out).length ? out : null;
}

// ── labs ────────────────────────────────────────────────────────────────────
//
// `[ { "name": "WBC", "value": "8.2", "unit": "10^3/µL", "ref": "4-11" } ]`
//
// `value` is a STRING, deliberately, and this is the reason the whole structured
// payload is a JSON string rather than a google.protobuf.Struct: Struct's
// numbers are doubles, so «8.20» comes back as 8.2 and «<0.01» cannot be stored
// at all. A lab value is a printed reading, not an arithmetic quantity — nothing
// in this app computes with one.

export const LAB_FIELDS = ['name', 'value', 'unit', 'ref'];

export const emptyLabRow = () => ({ name: '', value: '', unit: '', ref: '' });

/** Stored payload → editable rows. Always at least one blank row to type into. */
export function labsToForm(structured) {
  const rows = Array.isArray(structured) ? structured : [];
  const mapped = rows.map((r) =>
    Object.fromEntries(LAB_FIELDS.map((k) => [k, r?.[k] == null ? '' : String(r[k])])),
  );
  return mapped.length ? mapped : [emptyLabRow()];
}

/**
 * Rows → the stored payload, or null when nothing was typed.
 *
 * A row with no `name` is dropped: it is the blank row the editor always keeps
 * at the bottom, and storing it would grow the payload by one empty object per
 * save. A row WITH a name but no value is kept — "WBC was ordered, result
 * pending" is a real thing a student writes.
 */
export function formToLabs(rows) {
  const out = (Array.isArray(rows) ? rows : [])
    .map((r) => Object.fromEntries(LAB_FIELDS.map((k) => [k, String(r?.[k] ?? '').trim()])))
    .filter((r) => r.name)
    // Blank optional fields are dropped, for the same reason vitals drops them:
    // this JSON is interpolated into a prompt and `"unit": ""` is noise the
    // model has to read.
    .map((r) => Object.fromEntries(Object.entries(r).filter(([, v]) => v !== '')));
  return out.length ? out : null;
}

// ── entries ─────────────────────────────────────────────────────────────────

/**
 * Group a flat entry list by section id.
 *
 * Every section gets a key, including the empty ones — a screen that iterates
 * `HISTORY_SECTIONS` and reads `grouped[id]` must never hit undefined, and an
 * empty array is what the section's empty state renders from.
 */
export function groupBySection(entries) {
  const out = Object.fromEntries(SECTION_IDS.map((id) => [id, []]));
  for (const entry of Array.isArray(entries) ? entries : []) {
    if (out[entry?.section]) out[entry.section].push(entry);
  }
  for (const id of SECTION_IDS) {
    out[id].sort((a, b) => {
      const so = (a.sortOrder || 0) - (b.sortOrder || 0);
      if (so !== 0) return so;
      return Number(a.createdAtMs || 0) - Number(b.createdAtMs || 0);
    });
  }
  return out;
}

/** How many entries the case holds per section — the count the tab strip shows. */
export function sectionCounts(entries) {
  const grouped = groupBySection(entries);
  return Object.fromEntries(SECTION_IDS.map((id) => [id, grouped[id].length]));
}

/**
 * `manual | voice | ai` — how an entry came to exist.
 *
 * PROVENANCE, NOT PERMISSION. The AI never writes an entry: `parseCaseVoice`
 * returns a draft, the student edits and confirms it, and the client submits it
 * through `addCaseHistory` like anything they typed. This field is what lets a
 * student go back and re-check what a model put words in their mouth about, and
 * it is deliberately NOT updatable — an editable provenance field would let a
 * drafted entry become indistinguishable from a written one.
 */
export const SOURCES = ['manual', 'voice', 'ai'];
export const DEFAULT_SOURCE = 'manual';

/** The next `sortOrder` for a section, so a new entry lands at the bottom. */
export function nextSortOrder(entriesInSection) {
  const rows = Array.isArray(entriesInSection) ? entriesInSection : [];
  if (!rows.length) return 0;
  return Math.max(...rows.map((e) => Number(e.sortOrder) || 0)) + 1;
}

/**
 * Is this entry worth sending?
 *
 * The BFF stores an entry with an empty body only when `structured` carries
 * something. Checking here keeps the editor from writing a row that renders as
 * a blank card and can then only be deleted.
 */
export function entryHasContent(entry) {
  if (String(entry?.body ?? '').trim()) return true;
  const s = entry?.structured;
  if (Array.isArray(s)) return s.length > 0;
  if (s && typeof s === 'object') return Object.keys(s).length > 0;
  return false;
}
